// Captures des gestes de la page Consolidation (Captures > « Activités Consolidation »).
// Le contexte est figé au clic ; l'envoi part sans attendre. Une erreur n'atteint jamais le geste, son toast ni son état.
import { sendOperationFeedbackCapture } from "./operationFeedbackCapture.js";
import { createPerformanceCorrelationId } from "./sourceChangesRefreshPolicy.js";
import { driverQuestion, paidDrivers } from "./consolidationDriverCheck.js";
import { geotabTruckOf } from "./consolidationGeotabFill.js";
import { storyText } from "./dispatchChangeStory.js";

const STATUS = { saved: "success", validated: "success", applied: "success", opened: "success", draft_change: "success",
  awaiting_confirmation: "awaiting_confirmation", cancelled: "cancelled", nothing_to_add: "no_change",
  refused: "refused", partial: "partial", failed: "failed", save_unconfirmed: "failed" };
// Sans vrai toast : brouillon, ouverture, annulation et « rien à ajouter » restent une information.
const TOAST_TYPE = { saved: "success", validated: "success", applied: "success", partial: "warning", refused: "warning",
  awaiting_confirmation: "warning", failed: "error", save_unconfirmed: "error" };
const PLAN_SOURCES = ["closure", "latest", "none"];
const OFF_PLAN_KINDS = ["off", "depot", "loading"];
const DAY_LABEL = { today: "Ce jour", previous: "La veille" };
const SIGNAL_SUMMARY = { name_to_check: "Nom à vérifier", driver_to_confirm: "Chauffeur à confirmer", team_to_pay: "Équipe à payer", other_trucks: "Autre camion Geotab" };
const GEOTAB_STATE_SUMMARY = { loading: "Geotab en chargement", verifying: "Geotab en vérification", unavailable: "Geotab indisponible" };
const ANSWER_SUMMARY = { dispatch: "Chauffeur du Dispatch", same: "Même personne", other: "Autre chauffeur", team: "Équipe", removed: "Réponse retirée" };
export const NO_NEXT_SHEET = "Aucun relevé suivant";

const clip = (value, limit) => (typeof value === "string" ? value : value == null ? "" : String(value)).slice(0, limit);
const unique = (values, limit, max) => [...new Set(values.map(value => clip(value, limit).trim()).filter(Boolean))].slice(0, max);
const count = (value, ready = true) => (ready && Number.isInteger(value) ? Math.min(Math.max(value, 0), 500) : null);
const plural = (value, word) => `${value} ${word}${value > 1 ? "s" : ""}`;
const sheetKey = row => row.source_key || row.id;
const safe = fn => (...args) => { try { return fn(...args); } catch { return null; } };

// 409 : refusé ; réseau ou réponse incomplète après l'envoi : sauvegarde non confirmée ; sinon échec.
export const errorOutcome = error => (error?.saveUnconfirmed ? "save_unconfirmed" : Number(error?.status) === 409 ? "refused" : "failed");

// Camions conduits selon Geotab, en plus de celui du Dispatch.
export const otherTrucks = truck => (Array.isArray(truck?.trucks_used) ? truck.trucks_used : []).filter(used => used && !used.planned).map(used => used.name);

// Relevé (tournée ou commande sans tournée) : chauffeur et camion du Dispatch, ressource « chauffeur ».
export const sheetCaptureBase = safe(row => {
  const manual = Boolean(row.manual_order_id);
  const order = manual ? (row.visits || []).flatMap(visit => visit.orders || []).find(item => item?.id === row.manual_order_id) : null;
  return {
    date: row.dispatch_date, period: row.dispatch_period || "day", sheetKind: manual ? "manual_order" : "route",
    driverLabel: row.driver_name || null, trucks: [row.truck_number], resourceKind: "driver", resourceIds: row.id ? [row.id] : [],
    resourceLabel: manual ? `Commande sans tournée · ${order?.label || "commande"}` : `${row.driver_name || "Sans chauffeur"} · Camion ${row.truck_number || "non renseigné"}`,
  };
});

// Comparaison Geotab déjà chargée : sites prévus (chargements et livraisons), retrouvés, à vérifier, et arrêts hors plan
// (type et lieu, sans heure). Hors de « ready », les compteurs restent inconnus, jamais zéro.
export function geotabSummary(state, data = null, truck = null, fill = null) {
  const ready = state === "ready";
  const sites = (truck?.comparison?.sites || []).filter(site => site.kind === "loading" || site.kind === "delivery");
  const found = sites.filter(site => site.real_arrival != null);
  const offStops = truck?.comparison?.off_stops || [];
  return {
    state, plan_source: PLAN_SOURCES.includes(data?.plan_source) ? data.plan_source : null,
    planned: count(sites.length, ready), found: count(found.length, ready),
    to_check: count(found.filter(site => site.match === "address" || site.match === "place").length, ready),
    off_plan: ready ? offStops.slice(0, 30).map(stop => ({ type: stop.switch ? "truck_change" : OFF_PLAN_KINDS.includes(stop.kind) ? stop.kind : "off",
      place: stop.place?.name ? clip(stop.place.name, 160) : null })) : [],
    off_plan_total: count(offStops.length, ready),
    filled: count(fill?.filled, ready), replaced: count(fill?.replaced, ready), service: count(fill?.service, ready),
  };
}

// « 8 prévus / 8 retrouvés — 1 hors-plan » : la même phrase dans la capture et dans Captures.
export function geotabCounterLabel(geotab) {
  if (geotab?.planned == null || geotab.found == null) return "";
  const off = geotab.off_plan_total ? ` — ${geotab.off_plan_total} hors-plan` : "";
  return `${plural(geotab.planned, "prévu")} / ${plural(geotab.found, "retrouvé")}${off}`;
}

const OUTCOME_LABEL = { saved: "Sauvegardé", validated: "Validé", applied: "Appliqué", draft_change: "Modifié au brouillon",
  awaiting_confirmation: "À confirmer", cancelled: "Annulé", nothing_to_add: "Rien à ajouter", refused: "Refusé", failed: "Échec",
  partial: "Partiel", save_unconfirmed: "Sauvegarde non confirmée", opened: "Ouvert" };
const RECORD_LABEL = { ...OUTCOME_LABEL, remaining: "Non appliqué", blocked: "Bloqué", not_processed: "Non traité" };
const SIGNAL_LABEL = { ...SIGNAL_SUMMARY, other_trucks: "Autre(s) camion(s) Geotab" };
const GEOTAB_LABEL = { truck_not_found: "Camion introuvable dans Geotab", no_trips: "Aucun trajet Geotab ce jour-là",
  no_match: "Aucun arrêt Geotab ne correspond au relevé", unavailable: "Geotab indisponible" };
const PLAN_SOURCE_LABEL = { closure: "plan de la clôture", latest: "Dispatch courant", none: "sans plan" };
const OFF_PLAN_LABEL = { off: "Arrêt hors plan", depot: "Passage au dépôt", loading: "Chargement", truck_change: "Changement de camion" };

// Lecture d'une capture dans Captures : mêmes lignes sur la carte et dans « Copier pour analyse ».
export function buildConsolidationCaptureDetails(feedback) {
  const context = feedback?.consolidation_context;
  if (!context || typeof context !== "object") return null;
  const { geotab, advance, driver_answer: answer, loading_day: loadingDay, dispatch_changes: changes } = context;
  const period = feedback.dispatch_periods?.[0];
  const signals = [...(context.signals || []).map(signal => SIGNAL_LABEL[signal] || signal), GEOTAB_STATE_SUMMARY[context.geotab_state]
    || (context.geotab_state === "not_allowed" ? "Sans droit Geotab" : "")].filter(Boolean);
  const geotabDetail = geotab ? [PLAN_SOURCE_LABEL[geotab.plan_source] ? `Provenance : ${PLAN_SOURCE_LABEL[geotab.plan_source]}` : "",
    geotab.to_check ? `${geotab.to_check} à vérifier` : "",
    geotab.filled != null ? `${plural(geotab.filled, "arrêt")} à remplir${geotab.replaced ? `, ${geotab.replaced} remplacé(s)` : ""}${geotab.service ? ", heures de service" : ""}` : ""]
    .filter(Boolean).join(" · ") : "";
  return {
    tags: [context.draft ? "Modification au brouillon" : "", context.parent_event_id ? "Suite d’un clic précédent" : "",
      changes?.reopen ? "Réouverture autorisée" : ""].filter(Boolean),
    rows: [
      context.sheet_kind === "preload" ? { key: "driver", label: "Préchargements", value: plural(context.records?.length || 0, "préchargement") }
        : context.sheet_kind === "day" ? { key: "driver", label: "Journée", value: plural(changes?.groups?.length || 0, "changement") + " du Dispatch" }
          : { key: "driver", label: "Chauffeur", value: context.driver_label || feedback.resource_label || "—" },
      { key: "trucks", label: "Camions", value: (context.trucks || []).join(", ") || "—" },
      { key: "period", label: "Période", value: period === "evening" ? "Soir" : period === "day" ? "Jour" : "Journée" },
      { key: "outcome", label: "Résultat", value: OUTCOME_LABEL[context.outcome] || context.outcome },
      advance?.requested ? { key: "next", label: "Chauffeur suivant", value: advance.next_label || "Passage non observé" } : null,
      signals.length ? { key: "signals", label: "Signalements", value: signals.join(" · ") } : null,
      answer ? { key: "answer", label: "Réponse", value: ANSWER_SUMMARY[answer.kind] || answer.kind,
        detail: [answer.paid?.length ? `payé : ${answer.paid.join(" et ")}` : "", answer.geotab_name ? `session Geotab de ${answer.geotab_name}` : ""].filter(Boolean).join(" · ") } : null,
      loadingDay ? { key: "loading-day", label: "Ce jour / La veille", value: `${DAY_LABEL[loadingDay.previous]} → ${DAY_LABEL[loadingDay.next]}`,
        detail: [loadingDay.place, (loadingDay.orders || []).join(", ")].filter(Boolean).join(" · ") } : null,
      geotab ? { key: "geotab", label: "Geotab", value: geotab.state === "ready" ? geotabCounterLabel(geotab) : GEOTAB_LABEL[geotab.state] || geotab.state,
        detail: geotabDetail } : null,
    ].filter(Boolean),
    lists: [
      geotab?.off_plan?.length ? { key: "off-plan", label: `Hors plan (${geotab.off_plan_total ?? geotab.off_plan.length})`,
        items: geotab.off_plan.map(stop => [OFF_PLAN_LABEL[stop.type] || stop.type, stop.place].filter(Boolean).join(" · ")) } : null,
      context.records?.length ? { key: "records", label: context.sheet_kind === "day" ? "Relevés modifiés" : "Résultat par préchargement",
        items: context.records.map(record => `${record.label} — ${RECORD_LABEL[record.outcome] || record.outcome}`) } : null,
      changes?.groups?.length ? { key: "groups", label: "Changements du Dispatch",
        items: changes.groups.map(group => `${group.type ? `${group.type} · ` : ""}${group.label} — ${RECORD_LABEL[group.outcome] || group.outcome}`) } : null,
    ].filter(Boolean),
  };
}

// Signalements d'un relevé, calculés comme le relevé : brouillon en cours, employés, réponse enregistrée, camions Geotab.
export function driverSignals(sheet, { draft = null, geotabData = null, employees = [] } = {}) {
  const row = draft?.row || sheet;
  const truck = geotabTruckOf(geotabData, row);
  const ask = row.status === "draft" ? driverQuestion(row, truck, employees, (draft?.baseline || sheet).driver_check) : null;
  return [...(ask ? [ask.team ? "team_to_pay" : ask.spelling ? "name_to_check" : "driver_to_confirm"] : []),
    ...(otherTrucks(truck).length ? ["other_trucks"] : [])];
}

// Passage « Ce jour / La veille » d'un chargement : tout le passage (une ligne par commande) change ensemble.
export function loadingDayContext(visit, group, next) {
  return { place: clip(visit.label || "Lieu non renseigné", 160), next,
    orders: unique((group.length ? group : [visit]).flatMap(item => item.orders.map(order => order.label)), 128, 60),
    previous: visit.status === "inherited" ? "previous" : "today" };
}

// Groupes de changements du Dispatch au clic : texte et type de describeGroup ; les groupes liés ne sont pas tentés.
export function dispatchAcceptContext(stories, isBlocked) {
  return stories.slice(0, 30).map(({ group, story }) => ({ id: group.id, type: clip(story.type, 32), label: clip(storyText(story), 300),
    outcome: isBlocked(group) ? "blocked" : "remaining" }));
}

// Préchargements que le lot traitera, dans son ordre : « non traité » jusqu'à leur résultat.
export const recordsFromBatch = (entries, validate) => new Map(entries.filter(entry => entry.row.status === "draft" && (validate || entry.dirty))
  .map(entry => [entry.key, { label: clip(entry.label, 160), outcome: "not_processed" }]));

function consolidationContext({ outcome, draft = false, parentEventId = null, sheetKind, driverLabel = null, trucks = [], otherTrucks: extra = [],
  records = [], advance = null, signals = [], geotabState = null, driverAnswer = null, geotab = null, loadingDay = null, dispatchChanges = null }) {
  return {
    schema_version: 1, outcome, draft: Boolean(draft), sheet_kind: sheetKind,
    ...(parentEventId ? { parent_event_id: parentEventId } : {}),
    driver_label: driverLabel ? clip(driverLabel, 160) : null,
    trucks: unique([...trucks, ...extra], 64, 20),
    records: records.slice(0, 60).map(record => ({ label: clip(record.label, 160), outcome: record.outcome })),
    ...(advance ? { advance: { requested: Boolean(advance.requested), next_label: advance.next_label == null ? null : clip(advance.next_label, 160) } } : {}),
    signals: [...new Set(signals)].slice(0, 4),
    ...(geotabState ? { geotab_state: geotabState } : {}),
    ...(driverAnswer ? { driver_answer: { kind: driverAnswer.kind, paid: unique(driverAnswer.paid || [], 160, 2),
      geotab_name: driverAnswer.geotab_name ? clip(driverAnswer.geotab_name, 160) : null } } : {}),
    ...(geotab ? { geotab } : {}),
    ...(loadingDay ? { loading_day: loadingDay } : {}),
    ...(dispatchChanges ? { dispatch_changes: { reopen: Boolean(dispatchChanges.reopen),
      groups: dispatchChanges.groups.slice(0, 30).map(({ label, type, outcome: result }) => ({ label, type, outcome: result })) } } : {}),
  };
}

// Charge utile conforme au schéma serveur. Sans vrai toast : titre = libellé du geste, message = résumé d'une ligne.
export function buildConsolidationCapture({ eventId, action, outcome, totalMs = 0, toast = null, title = "", summary = "", steps = [],
  date = null, period = null, resourceKind = "driver", resourceIds = [], resourceLabel = "", ...context }) {
  const status = STATUS[outcome];
  const toastTitle = toast?.title ?? title;
  const message = toast ? toast.message || "" : clip(summary, 2000);
  const ids = unique(resourceIds, 128, 100);
  return {
    operation_event_id: eventId, action,
    toast_title: clip(toastTitle, 256), toast_message: clip(message, 16000),
    toast_type: ["success", "warning", "error", "info"].includes(toast?.type) ? toast.type : TOAST_TYPE[outcome] || "info",
    toast_truncated: toastTitle.length > 256 || message.length > 16000,
    status, client_total_ms: Math.max(0, Number(totalMs) || 0),
    dispatch_date: /^\d{4}-\d{2}-\d{2}$/.test(date || "") ? date : null,
    dispatch_periods: period === "day" || period === "evening" ? [period] : [],
    resource_kind: resourceKind, resource_ids: ids, resource_count: ids.length || null, resource_ids_truncated: false,
    resource_label: clip(resourceLabel, 512), audit_ids: [],
    steps: steps.filter(step => ["completed", "warning", "failed", "skipped"].includes(step.status)).slice(0, 16)
      .map(step => ({ key: clip(step.key, 64), label: clip(step.label, 160), status: step.status,
        duration_ms: Number.isFinite(step.duration_ms) ? Math.max(0, step.duration_ms) : null })),
    consolidation_context: consolidationContext({ outcome, ...context }),
  };
}

// Un geste : identifiant et départ figés au clic ; un seul envoi, à son issue finale.
export function startConsolidationCapture(action, base = {}, { now = () => performance.now(), send = sendOperationFeedbackCapture } = {}) {
  try {
    const started = now();
    const eventId = createPerformanceCorrelationId();
    let sent = false;
    return {
      eventId,
      finish(result = {}) {
        if (sent) return;
        sent = true;
        try {
          const payload = buildConsolidationCapture({ ...base, ...result, action, eventId, totalMs: now() - started });
          void Promise.resolve().then(() => send(payload)).catch(() => {});
        } catch { /* La capture n'interrompt jamais le geste. */ }
      },
    };
  } catch {
    return { eventId: null, finish() {} };
  }
}

// Issue finale de Sauvegarder / Valider d'un relevé (useActionFeedback) : toast montré et étapes de sa progression.
export function sheetActionResult(verb, { state, error, toast, steps = [] } = {}) {
  const outcome = state === "success" ? (verb === "validate" ? "validated" : "saved") : state === "refresh_failed" ? "partial"
    : state === "handled" ? "awaiting_confirmation" : errorOutcome(error);
  // Un conflit ou un refus n'est pas une panne : l'étape interrompue reste un avertissement.
  const warn = outcome === "awaiting_confirmation" || outcome === "refused";
  return { outcome, toast, steps: steps.map(step => (warn && step.status === "failed" && step.key !== "refresh" ? { ...step, status: "warning" } : step)),
    ...(outcome === "awaiting_confirmation" ? { summary: "Conflit de quantités : choisir les valeurs à valider." } : {}) };
}

// Ouverture d'un chauffeur dans la marge gauche : seulement s'il porte un signalement, ou si Geotab est inconnu ou pas encore vérifié
// (« verifying » : comparatif gardé affiché pendant sa relecture ; « ready » : comparatif vérifié).
export const captureDriverOpen = safe((capture, item, { readDraft, employees = [], geotab = null, label = "" } = {}) => {
  if (!capture) return null;
  const sheets = item.routes.map(route => route.sheet);
  const geotabState = !geotab ? "not_allowed" : geotab.busy ? "loading" : geotab.error ? "unavailable" : geotab.verified ? "ready" : "verifying";
  const signals = [...new Set(sheets.flatMap(sheet => driverSignals(sheet, { draft: readDraft?.(sheetKey(sheet)), geotabData: geotab?.data, employees })))];
  if (!signals.length && !["loading", "verifying", "unavailable"].includes(geotabState)) return null;
  const first = sheets[0] || {};
  const tracked = capture.start("consolidation_driver_open", {
    date: first.dispatch_date, period: first.dispatch_period || "day", sheetKind: "route", driverLabel: label || item.label,
    trucks: [...item.trucks, ...sheets.flatMap(sheet => otherTrucks(geotabTruckOf(geotab?.data, sheet)))],
    resourceKind: "driver", resourceIds: sheets.map(sheet => sheet.id).filter(Boolean), resourceLabel: label || item.label,
  });
  tracked.finish({ outcome: "opened", signals, geotabState, title: "Ouverture du chauffeur",
    summary: [label || item.label, ...signals.map(signal => SIGNAL_SUMMARY[signal]), GEOTAB_STATE_SUMMARY[geotabState]].filter(Boolean).join(" · ") });
  if (tracked.eventId) sheets.forEach(sheet => capture.openings.set(sheetKey(sheet), tracked.eventId));
  return tracked.eventId;
});

// Réponse « Qui a conduit ? » : confirmée ou retirée (au brouillon), ou fenêtre annulée ; reliée à l'ouverture capturée.
export const captureDriverAnswer = safe((capture, row, outcome, check, employees = []) => {
  if (!capture) return;
  const kind = check?.answer || "removed";
  const paid = !check ? [] : ["other", "team"].includes(kind) ? paidDrivers(check, employees).map(driver => driver.name) : [row.driver_name];
  capture.start("consolidation_driver_answer", { ...sheetCaptureBase(row), parentEventId: capture.openings.get(sheetKey(row)) || null }).finish({
    outcome, draft: outcome === "draft_change", driverAnswer: { kind, paid, geotab_name: (check || row.driver_check)?.geotab_name },
    title: outcome === "cancelled" ? "Annuler" : check ? "Confirmer" : row.driver_check?.answer === "other" ? "Annuler" : "Changer",
    summary: `${ANSWER_SUMMARY[kind]}${paid.length ? ` · payé : ${paid.join(" et ")}` : ""}${outcome === "cancelled" ? " · non retenu" : " · modification au brouillon"}`,
  });
});

// « Ce jour / La veille » : aucune capture si la valeur ne change pas.
export const captureLoadingDay = safe((capture, row, visit, group, next) => {
  const loadingDay = capture && visit ? loadingDayContext(visit, group, next) : null;
  if (!loadingDay || loadingDay.previous === next) return;
  capture.start("consolidation_loading_day", { ...sheetCaptureBase(row), loadingDay }).finish({ outcome: "draft_change", draft: true,
    title: DAY_LABEL[next], summary: `${loadingDay.place} : ${DAY_LABEL[loadingDay.previous]} → ${DAY_LABEL[next]} · ${plural(loadingDay.orders.length, "commande")}` });
});

// « Ajouter les heures Geotab » : issue du clic ; la décision (Ajouter, Annuler) suit avec ce clic pour parent.
export const finishGeotabFill = safe((tracked, state, { data = null, truck = null, result = null, message = "" } = {}) => {
  if (!tracked) return null;
  const geotab = geotabSummary(state, data, truck, result);
  const ready = state === "ready";
  tracked.finish({ outcome: ready ? "awaiting_confirmation" : state === "unavailable" ? "failed" : "nothing_to_add", geotab, otherTrucks: otherTrucks(truck),
    summary: ready ? `${geotabCounterLabel(geotab)} · ${plural(result.filled, "arrêt")} à remplir · à confirmer` : message });
  return ready && tracked.eventId ? { parentEventId: tracked.eventId, geotab, otherTrucks: otherTrucks(truck) } : null;
});

export const captureGeotabDecision = safe((capture, decision, row, added) => {
  if (!capture || !decision) return;
  const filled = decision.geotab.filled ?? 0;
  capture.start("consolidation_geotab_decision", { ...sheetCaptureBase(row), ...decision }).finish({ outcome: added ? "draft_change" : "cancelled",
    draft: added, title: added ? "Ajouter les heures Geotab" : "Annuler",
    summary: added ? `${plural(filled, "arrêt")} ${filled > 1 ? "remplis" : "rempli"} au brouillon` : "Heures Geotab non ajoutées" });
});

// Lot de préchargements : une capture par clic, une ligne par préchargement dans l'ordre du lot.
export const startPreloadBatchCapture = safe((capture, entries, validate) => {
  if (!capture) return null;
  const records = recordsFromBatch(entries, validate);
  const rows = entries.filter(entry => records.has(entry.key)).map(entry => entry.row);
  const tracked = capture.start(validate ? "consolidation_preload_validate" : "consolidation_preload_save", {
    date: rows[0]?.dispatch_date || rows[0]?.delivery_date, sheetKind: "preload", resourceKind: "dispatch",
    resourceIds: rows.map(row => row.id).filter(Boolean), resourceLabel: "Préchargements",
    trucks: rows.flatMap(row => (row.links || []).map(link => link.truck_number)),
  });
  let current = null;
  const done = validate ? "validated" : "saved";
  return {
    // Préchargement en cours, pour attribuer une erreur à la bonne ligne.
    watch: safe(list => list.map(entry => ({ ...entry, save: (...args) => { current = { key: entry.key, step: "saved" }; return entry.save(...args); },
      validate: (...args) => { current = { key: entry.key, step: "validated" }; return entry.validate(...args); } }))),
    report: safe((entry, kind) => { if (records.has(entry.key)) records.get(entry.key).outcome = kind; }),
    finish: safe(error => {
      const record = error && current ? records.get(current.key) : null;
      if (record && record.outcome !== current.step) record.outcome = errorOutcome(error);
      const list = [...records.values()];
      const processed = list.filter(item => item.outcome === "saved" || item.outcome === "validated").length;
      const outcome = !error ? done : processed ? "partial" : errorOutcome(error);
      const left = list.length - processed;
      tracked.finish({ outcome, records: list, title: validate ? "Valider les préchargements" : "Sauvegarder",
        summary: [`${processed}/${list.length} ${validate ? "validé(s)" : "enregistré(s)"}`, left ? `${left} non traité(s)` : "",
          error ? `${error.saveUnconfirmed ? "Sauvegarde non confirmée. " : ""}${error.message || "Erreur"}` : ""].filter(Boolean).join(" · ") });
    }),
  };
});

// « Accepter la modification » : groupes figés au clic ; chaque groupe appliqué ajoute ses relevés.
export const startDispatchAcceptCapture = safe((capture, { date, stories, isBlocked, reopen }) => {
  if (!capture) return null;
  const groups = new Map(dispatchAcceptContext(stories, isBlocked).map(group => [group.id, group]));
  const records = new Map();
  const trucks = new Set();
  const tracked = capture.start("consolidation_dispatch_accept", { date, sheetKind: "day", resourceKind: "dispatch", resourceLabel: `Journée du ${date}` });
  const finish = (outcome, error = null) => {
    const list = [...groups.values()];
    const applied = list.filter(group => group.outcome === "applied").length;
    tracked.finish({ outcome, trucks: [...trucks], records: [...records.values()], dispatchChanges: { groups: list, reopen },
      title: "Accepter la modification",
      summary: [`${applied}/${list.filter(group => group.outcome !== "blocked").length} groupe(s) appliqué(s)`,
        list.some(group => group.outcome === "blocked") ? `${list.filter(group => group.outcome === "blocked").length} bloqué(s)` : "",
        error?.message || ""].filter(Boolean).join(" · ") });
  };
  return {
    applied: safe((ids, rows = []) => {
      ids.forEach(id => { if (groups.has(id)) groups.get(id).outcome = "applied"; });
      rows.forEach(row => {
        records.set(sheetKey(row), { label: [row.driver_name || row.employee_name, row.truck_number ? `Camion ${row.truck_number}` : ""].filter(Boolean).join(" · ") || "Relevé", outcome: "applied" });
        if (row.truck_number) trucks.add(row.truck_number);
      });
    }),
    // Un groupe refusé par le serveur reste « remaining » ; le premier motif suit le résumé.
    done: safe((refused = []) => finish(refused.length ? "partial" : "applied", refused[0] || null)),
    refused: safe(error => finish("refused", error)),
    fail: safe(error => finish([...groups.values()].some(group => group.outcome === "applied") ? "partial" : errorOutcome(error), error)),
  };
});
